import { useState } from "react";
import { Info } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { MAX_RANGE_DAYS, daysAgoISODate, todayISODate } from "@/stores/filterStore";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import SectionRangePicker from "@/components/SectionRangePicker";
import Panel from "./Panel";
import Skeleton from "./Skeleton";

// MyStyle 운영 관측 (Phase 2A P0, 2026-09-15).
// 정본 = mystyle_perf CloudWatch Logs (novadb 백엔드 GET /analytics/mystyle 경유).
// 이번 Phase 는 비용/NC 연결을 하지 않는다 — 채택·성능·정합성만 본다.
// 새 거대 대시보드가 아니라 기존 Dashboard 그룹의 탭 하나로 흡수한다.

function defaultRange() {
  return { from: daysAgoISODate(6), to: todayISODate() };
}

// 값이 없을 때(로그 0건 or 조회 실패) 카드가 빈칸 대신 명시적으로 보이게.
function fmtInt(v) {
  return v == null ? "—" : Number(v).toLocaleString();
}
function fmtPct(v) {
  return v == null ? "—" : `${Number(v).toFixed(1)}%`;
}
function fmtMs(v) {
  if (v == null) return "—";
  const n = Number(v);
  return n >= 1000 ? `${(n / 1000).toFixed(2)}s` : `${Math.round(n)}ms`;
}
function fmtUsd(v) {
  return v == null ? "—" : `$${Number(v).toFixed(4)}`;
}
function fmtKrw(v) {
  return v == null ? "—" : `₩${Number(v).toLocaleString()}`;
}

export default function MyStyleSection() {
  const [range, setRange] = useState(defaultRange);

  const { data, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.mystyle(p, s),
    [range.from, range.to],
    range
  );

  const m = data?.metrics;
  const cost = data?.cost;
  const logStatus = data?.logStatus;
  const lat = m?.latency;
  const cache = m?.cache;
  const appliesToPool = data?.appliesToPool !== false; // 미지정이면 적용으로 간주(하위호환)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <Info className="h-4 w-4 text-brand-500" />
          <span>
            MyStyle 운영 현황 — 사용/성능/적용: <code className="rounded bg-ink-100 px-1">mystyle_perf</code> 로그,
            토큰/NC/원가: <code className="rounded bg-ink-100 px-1">mystyle_reservation</code>(settled). 두 소스는
            역할이 분리돼 합산하지 않습니다. 성능·캐시 지표는 <strong>실제 적용(ON)</strong> 요청만 대상.
          </span>
        </div>
        <SectionRangePicker
          from={range.from}
          to={range.to}
          onChange={(from, to) => setRange({ from, to })}
        />
      </div>

      {/* MyStyle 은 nova 전용 — sedaily 조회 시 지표가 성립하지 않음을 명시 */}
      {!appliesToPool && (
        <Panel>
          <div className="py-8 text-center text-sm text-ink-400">
            MyStyle 은 Nova(ainova.news) 전용 기능입니다. 선택한 풀에는 MyStyle 사용 데이터가 없습니다 —
            풀을 <strong>nova</strong> 또는 <strong>전체</strong>로 바꿔 주세요.
          </div>
        </Panel>
      )}

      {/* 로그 조회 상태 안내 — '0건'과 '조회 실패'를 구분해 보여준다 */}
      {appliesToPool && logStatus?.error && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          CloudWatch 로그 조회에 실패했습니다({logStatus.error}). 아래 수치는 신뢰할 수 없습니다 —
          로그 그룹·기간·권한을 확인해 주세요.
        </div>
      )}
      {appliesToPool && logStatus?.truncated && !logStatus?.error && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          로그가 조회 예산 안에 다 읽히지 않아 일부만 집계됐습니다(기간을 좁혀 주세요).
        </div>
      )}

      {!appliesToPool ? null : loading && !data ? (
        <Skeleton />
      ) : error ? (
        <Panel>
          <div className="py-8 text-center text-sm text-ink-400">
            조회 중 오류가 발생했습니다: {String(error.message || error)}
          </div>
        </Panel>
      ) : (
        <>
          <Panel title="채택 · 정합성 (분모 = 전체 관측 요청, ON+OFF)">
            <KpiRow cols={4}>
              <KpiCard
                label="MyStyle 요청 수 (관측)"
                value={fmtInt(m?.observedRequests)}
                tone="brand"
                hero
                sublabel="기간 내 mystyle_perf 로그(ON+OFF, 중복 제거)"
              />
              <KpiCard
                label="실제 적용 수"
                value={fmtInt(m?.applied)}
                tone="default"
                sublabel="mystyle_on = true (profileApplied)"
              />
              <KpiCard
                label="Adoption Rate"
                value={fmtPct(m?.adoptionRatePct)}
                tone="good"
                sublabel="적용 / 전체 관측 요청"
              />
              <KpiCard
                label="Intent Mismatch"
                value={fmtInt(m?.intentMismatch)}
                tone="warn"
                invert
                sublabel="의도 ON · 실제 OFF (UI↔backend)"
              />
            </KpiRow>
            <KpiRow cols={4}>
              <KpiCard
                label="사용자 의도 ON"
                value={fmtInt(m?.requested)}
                tone="default"
                sublabel="mystyle_requested = true"
              />
              <KpiCard
                label="Apply Success Rate"
                value={fmtPct(m?.applySuccessRatePct)}
                tone="good"
                sublabel="적용∩의도 / 의도 ON 요청 (≤100%)"
              />
            </KpiRow>
          </Panel>

          <Panel title="성능 · 캐시 (분모 = 실제 적용 ON 요청만)">
            <KpiRow cols={4}>
              <KpiCard
                label="Latency p50"
                value={fmtMs(lat?.totalP50)}
                tone="default"
                sublabel={`ON 표본 ${fmtInt(m?.appliedSampleCount)}건`}
              />
              <KpiCard
                label="Latency p95"
                value={fmtMs(lat?.totalP95)}
                tone="default"
                sublabel="total_ms 분포 상위 5% (ON only)"
              />
              <KpiCard
                label="생성 p50"
                value={fmtMs(lat?.generationP50)}
                tone="default"
                sublabel="generation_ms (ON only)"
              />
              <KpiCard
                label="Cache Hit Rate"
                value={cache?.hasData ? fmtPct(cache?.hitRatePct) : "—"}
                tone="good"
                sublabel="cache_read / (read + input), ON only"
              />
            </KpiRow>
          </Panel>

          <Panel title="비용 · NC · 원가 (mystyle_reservation · settled 만)">
            <KpiRow cols={4}>
              <KpiCard
                label="MyStyle ON 요청 수 (과금)"
                value={fmtInt(cost?.requests)}
                tone="brand"
                sublabel="settled reservation (미정산·환불 제외)"
              />
              <KpiCard
                label="Total NC"
                value={fmtInt(cost?.chargedNc)}
                tone="default"
                sublabel="charged_nc 합계"
              />
              <KpiCard
                label="Avg NC / Request"
                value={fmtInt(cost?.avgNcPerRequest)}
                tone="default"
                sublabel="Total NC / 요청 수"
              />
              <KpiCard
                label="Estimated AI Cost"
                value={fmtUsd(cost?.usdCost)}
                tone="warn"
                invert
                sublabel={`${fmtKrw(cost?.krwCost)} · pricing 재사용`}
              />
            </KpiRow>
            <KpiRow cols={4}>
              <KpiCard
                label="Avg Cost / Request"
                value={fmtUsd(cost?.avgUsdPerRequest)}
                tone="warn"
                invert
                sublabel={fmtKrw(cost?.avgKrwPerRequest)}
              />
              <KpiCard
                label="Input Tokens"
                value={fmtInt(cost?.inputTokens)}
                tone="default"
                sublabel="usage_meta gen+rev"
              />
              <KpiCard
                label="Output Tokens"
                value={fmtInt(cost?.outputTokens)}
                tone="default"
                sublabel="usage_meta gen+rev"
              />
              <KpiCard
                label="Cache Read / Write"
                value={cost?.cacheTokensAvailable ? `${fmtInt(cost?.cacheReadTokens)} / ${fmtInt(cost?.cacheWriteTokens)}` : "n/a"}
                tone="default"
                sublabel="reservation 행엔 없음 → 성능탭 캐시 참조"
              />
            </KpiRow>
            {(cost?.byEngine?.length || 0) > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-xs font-medium text-ink-500">engine별 (charged NC 내림차순)</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs tabular-nums">
                    <thead>
                      <tr className="text-ink-400">
                        <th className="py-1 text-left font-medium">engine</th>
                        <th className="py-1 text-right font-medium">요청</th>
                        <th className="py-1 text-right font-medium">NC</th>
                        <th className="py-1 text-right font-medium">in tok</th>
                        <th className="py-1 text-right font-medium">out tok</th>
                        <th className="py-1 text-right font-medium">원가(USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cost.byEngine.map((e) => (
                        <tr key={e.key} className="border-t border-ink-100">
                          <td className="py-1 text-left text-ink-700">{e.key}</td>
                          <td className="py-1 text-right">{fmtInt(e.requests)}</td>
                          <td className="py-1 text-right">{fmtInt(e.chargedNc)}</td>
                          <td className="py-1 text-right">{fmtInt(e.inputTokens)}</td>
                          <td className="py-1 text-right">{fmtInt(e.outputTokens)}</td>
                          <td className="py-1 text-right">{fmtUsd(e.usdCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(cost?.excludedNonSettled || cost?.dedupedDuplicates) ? (
              <p className="mt-3 text-[11px] text-ink-400">
                제외: 미정산/환불 {fmtInt(cost?.excludedNonSettled)}건 · 중복 transactionId {fmtInt(cost?.dedupedDuplicates)}건
              </p>
            ) : null}
          </Panel>

          <Panel title="참고 (ON 요청 기준)">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-ink-500 sm:grid-cols-4">
              <div>
                <div className="text-ink-400">first_visible p50 / p95</div>
                <div className="mt-0.5 text-sm font-semibold text-ink-800 tabular-nums">
                  {fmtMs(lat?.firstVisibleP50)} / {fmtMs(lat?.firstVisibleP95)}
                </div>
              </div>
              <div>
                <div className="text-ink-400">Revision 발생 (ON 중)</div>
                <div className="mt-0.5 text-sm font-semibold text-ink-800 tabular-nums">
                  {fmtInt(m?.revisionCount)} ({fmtPct(m?.revisionRatePct)})
                </div>
              </div>
              <div>
                <div className="text-ink-400">Cache read / write tok</div>
                <div className="mt-0.5 text-sm font-semibold text-ink-800 tabular-nums">
                  {fmtInt(cache?.readTokens)} / {fmtInt(cache?.writeTokens)}
                </div>
              </div>
              <div>
                <div className="text-ink-400">누락 request_id / malformed</div>
                <div className="mt-0.5 text-sm font-semibold text-ink-800 tabular-nums">
                  {fmtInt(m?.missingRequestId)} / {fmtInt(m?.malformed)}
                </div>
              </div>
              <div>
                <div className="text-ink-400">requested 필드 보유 / 전체</div>
                <div className="mt-0.5 text-sm font-semibold text-ink-800 tabular-nums">
                  {fmtInt(m?.requestedFieldPresentCount)} / {fmtInt(m?.observedRequests)}
                </div>
              </div>
              <div>
                <div className="text-ink-400">적용∩의도 (success 분자)</div>
                <div className="mt-0.5 text-sm font-semibold text-ink-800 tabular-nums">
                  {fmtInt(m?.appliedRequested)} / {fmtInt(m?.requested)}
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-ink-400">
              Apply Success Rate 는 적용∩의도(appliedRequested) / 의도 ON 요청으로 계산해 100% 를
              넘지 않습니다. 과거 로그(mystyle_requested 필드 없음)는 의도 집합에 포함하지 않으며,
              "requested 필드 보유 / 전체"로 신규·과거 로그 혼합 정도를 확인할 수 있습니다.
              채택 지표(관측 요청·의도·적용·mismatch)는 ON+OFF 전체가 분모이고, 성능·캐시·revision
              지표는 실제 적용(mystyle_on=true)된 요청만 분모입니다 — OFF streaming 요청은 성능 KPI 에
              섞이지 않습니다. 데이터는 CloudWatch Logs 보존기간 내에서만 조회되며, MyStyle 은 nova
              전용이라 풀 선택과 무관하게 nova 로그 기준입니다.
            </p>
          </Panel>
        </>
      )}
    </div>
  );
}
